import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  Speed as SpeedIcon,
  Event as DateIcon,
  Search as SearchIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { Vehicle, VehicleStatus } from '../../types';
import { addVehicle, getAllVehicles, updateVehicle, deleteVehicle } from '../../services/vehicleService';
import { getAllRentals } from '../../services/rentalService';
import { db } from '../../config/firebase';
import { onSnapshot, collection } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService'; // Import the subscriptionService
import VehicleDetailDialog from './VehicleDetailDialog'; // Import VehicleDetailDialog
import { useNavigate } from 'react-router-dom';
import { VehicleCard } from './VehicleCard';
import ExportButton from '../common/ExportButton';
import { prepareVehiclesForExport } from '../../services/exportService';
import { format } from 'date-fns';

const initialState: Omit<Vehicle, 'id'> = {
  userId: '',
  brand: '',
  model: '',
  year: new Date().getFullYear(),
  registration: '',
  status: 'available',
  dailyRate: 0,
  baseMileage: 0,
  fuelType: '',
  imageUrl: '',
};

const VehicleList: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialState);
  const [activeRentals, setActiveRentals] = useState<string[]>([]);

  useEffect(() => {
    console.log('VehicleList - Component mounted');
    let isSubscribed = true;

    const loadInitialVehicles = async () => {
      try {
        if (!currentUser?.uid || !isSubscribed) return;
        const vehiclesData = await getAllVehicles(currentUser.uid);
        if (isSubscribed) {
          setVehicles(vehiclesData);
        }
      } catch (error) {
        console.error('Error loading initial vehicles:', error);
      }
    };

    // Charger les véhicules initiaux
    loadInitialVehicles();

    // Configurer l'écouteur Firestore
    const unsubscribe = onSnapshot(
      collection(db, 'vehicles'),
      (snapshot) => {
        if (!isSubscribed) return;
        
        console.log('Firestore - Vehicle collection update detected');
        loadInitialVehicles();
      },
      (error) => {
        console.error('Firestore - Error listening to vehicles:', error);
      }
    );

    return () => {
      console.log('VehicleList - Component unmounting, cleaning up subscriptions');
      isSubscribed = false;
      unsubscribe();
    };
  }, [currentUser]); // Dépendance à currentUser

  useEffect(() => {
    console.log('Filtering vehicles:', {
      totalVehicles: vehicles.length,
      searchTerm,
    });
    
    const filtered = vehicles.filter(vehicle => 
      vehicle.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.registration.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    console.log('Filtered vehicles:', filtered.length);
    setFilteredVehicles(filtered);
  }, [vehicles, searchTerm]);

  const loadVehicles = async () => {
    if (!currentUser?.uid) return;  // Add guard clause
    
    try {
      console.log('Loading vehicles and rentals...');
      const [vehiclesData, rentalsData] = await Promise.all([
        getAllVehicles(currentUser.uid),
        getAllRentals(currentUser.uid)
      ]);

      // Vérifier que chaque véhicule a un ID et un statut valide
      const validVehicles = vehiclesData.filter(vehicle => {
        if (!vehicle.id) {
          console.error('Vehicle without ID:', vehicle);
          return false;
        }
        
        // Vérifier si le statut est valide
        if (!vehicle.status || !['available', 'rented', 'maintenance', 'unavailable', 'reservation'].includes(vehicle.status)) {
          console.error('Vehicle with invalid status:', vehicle);
          vehicle.status = 'available';  // Définir un statut par défaut
        }
        
        return true;
      });

      console.log('Initial vehicles data:', validVehicles.map(v => ({
        id: v.id,
        brand: v.brand,
        model: v.model,
        status: v.status
      })));

      // Récupérer les IDs des véhicules actuellement en location active ou en réservation
      const rentedVehicleIds = rentalsData
        .filter(rental => rental.status === 'active')
        .map(rental => rental.vehicleId);

      const reservedVehicleIds = rentalsData
        .filter(rental => rental.status === 'reservation')
        .map(rental => rental.vehicleId);
      
      console.log('Active rental vehicle IDs:', rentedVehicleIds);
      console.log('Reserved vehicle IDs:', reservedVehicleIds);

      // Mettre à jour le statut des véhicules
      const updatedVehicles = validVehicles.map(vehicle => {
        if (!vehicle.id) return vehicle;

        const isRented = rentedVehicleIds.includes(vehicle.id);
        const isReserved = reservedVehicleIds.includes(vehicle.id);
        const currentStatus = vehicle.status || 'available';

        console.log(`Processing vehicle ${vehicle.id}:`, {
          brand: vehicle.brand,
          model: vehicle.model,
          currentStatus,
          isRented,
          isReserved
        });

        // Si le véhicule est en location active
        if (isRented) {
          console.log(`Setting ${vehicle.brand} ${vehicle.model} to rented`);
          return {
            ...vehicle,
            status: 'rented' as const
          };
        }

        // Si le véhicule est réservé
        if (isReserved) {
          console.log(`Setting ${vehicle.brand} ${vehicle.model} to reservation`);
          return {
            ...vehicle,
            status: 'reservation' as const
          };
        }

        // Si le véhicule n'est ni en location ni réservé et n'est pas en maintenance
        if (!isRented && !isReserved && currentStatus !== 'maintenance') {
          console.log(`Setting ${vehicle.brand} ${vehicle.model} to available`);
          return {
            ...vehicle,
            status: 'available' as const
          };
        }

        return vehicle;
      });

      console.log('Final vehicle statuses:', updatedVehicles.map(v => ({
        id: v.id,
        brand: v.brand,
        model: v.model,
        status: v.status
      })));

      setVehicles(updatedVehicles);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleOpen = async (vehicle?: Vehicle) => {
    if (!vehicle) {
      // Vérifier si l'utilisateur peut ajouter un nouveau véhicule
      const canAdd = await subscriptionService.canAddVehicle(currentUser?.uid || '', vehicles.length);
      if (!canAdd) {
        alert('Vous avez atteint la limite de véhicules pour votre plan d\'abonnement. Veuillez mettre à niveau votre plan pour ajouter plus de véhicules.');
        return;
      }
    }

    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        brand: vehicle.brand,
        model: vehicle.model,
        year: vehicle.year,
        registration: vehicle.registration,
        status: vehicle.status,
        dailyRate: vehicle.dailyRate,
        baseMileage: vehicle.baseMileage,
        fuelType: vehicle.fuelType,
        userId: vehicle.userId,
      });
    } else {
      setEditingVehicle(null);
      setFormData(initialState);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingVehicle(null);
  };

  const handleCardClick = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setDetailDialogOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'year' || name === 'dailyRate' || name === 'baseMileage' ? Number(value) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!currentUser?.uid) return;  // Add guard clause
      
      if (editingVehicle?.id) {
        // Si le véhicule est en cours de location, ne pas modifier son statut
        const isRented = activeRentals.includes(editingVehicle.id);
        const dataToUpdate = {
          ...formData,
          status: isRented ? 'rented' : formData.status
        };
        await updateVehicle(editingVehicle.id, dataToUpdate);
      } else {
        // Ajouter le userId aux données du véhicule
        await addVehicle({
          ...formData,
          userId: currentUser.uid
        });
      }
      handleClose();
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await deleteVehicle(id);
        loadVehicles();
      } catch (error) {
        console.error('Error deleting vehicle:', error);
      }
    }
  };

  const handleStatusToggle = (e: React.MouseEvent, vehicle: Vehicle) => {
    e.stopPropagation(); // Empêcher la propagation du clic
    const newStatus = vehicle.status === 'available' ? 'maintenance' : 'available';
    updateVehicle(vehicle.id!, { ...vehicle, status: newStatus });
  };

  const handleEditClick = (e: React.MouseEvent, vehicle: Vehicle) => {
    e.stopPropagation(); // Empêcher la propagation du clic
    handleOpen(vehicle);
  };

  const handleDeleteClick = (e: React.MouseEvent, vehicle: Vehicle) => {
    e.stopPropagation(); // Empêcher la propagation du clic
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) {
      deleteVehicle(vehicle.id!);
    }
  };

  const handleTrackingClick = (vehicleId: string | undefined) => {
    if (!vehicleId) return;
    navigate(`/vehicles/${vehicleId}/tracking`);
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    handleOpen(vehicle);
  };

  const handleDeleteVehicle = async (vehicleId: string) => {
    handleDelete(vehicleId);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <ExportButton 
          data={prepareVehiclesForExport(vehicles)}
          fileName={`vehicules_${format(new Date(), 'dd-MM-yyyy')}`}
          tooltipTitle="Exporter la liste des véhicules"
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Ajouter un véhicule
        </Button>
      </Box>

      <Grid container spacing={3}>
        {filteredVehicles.map((vehicle) => (
          <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
            <VehicleCard
              vehicle={vehicle}
              onEdit={handleEditVehicle}
              onDelete={handleDeleteVehicle}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="brand"
              label="Marque"
              type="text"
              fullWidth
              value={formData.brand}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="model"
              label="Modèle"
              type="text"
              fullWidth
              value={formData.model}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="year"
              label="Année"
              type="number"
              fullWidth
              value={formData.year}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="registration"
              label="Immatriculation"
              type="text"
              fullWidth
              value={formData.registration}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="baseMileage"
              label="Kilométrage initial"
              type="number"
              fullWidth
              value={formData.baseMileage}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="status"
              label="État"
              select
              fullWidth
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <MenuItem value="available">Disponible</MenuItem>
              <MenuItem value="rented">En location</MenuItem>
              <MenuItem value="unavailable">Non disponible</MenuItem>
              <MenuItem value="reservation">Réservé</MenuItem>
            </TextField>
            <TextField
              margin="dense"
              name="dailyRate"
              label="Tarif journalier (DZD)"
              type="number"
              fullWidth
              value={formData.dailyRate}
              onChange={handleInputChange}
              required
            />
            <TextField
              margin="dense"
              name="fuelType"
              label="Type de carburant"
              type="text"
              fullWidth
              value={formData.fuelType}
              onChange={handleInputChange}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingVehicle ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {selectedVehicle && (
        <VehicleDetailDialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          vehicle={selectedVehicle}
        />
      )}
    </Box>
  );
};

export default VehicleList;
